import React, { useState, useEffect, useContext, useRef } from "react";
import moment from "moment";
import { Line, Bar } from "react-chartjs-2";
import FETCH from "../../../utils/fetch";
import "../AssetsDashboard.css";
import assetHomeIcon from "../../../assets/images/asset.png";
import NewLoader from "../../../components/styled-components/loader/NewLoader";
import { ChartColors } from "../../../components/ChartColors";
import DASHBOARD_API from "../../../apis/dashboard.api";
import getFAClientName from "../../../helpers/getFAClientName";
import { GlobalLoaderContext } from "../../../utils/GlobalLoaderContext";
import { AssetsPDFReport } from "./AssetsPDFReport";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import DEATH_API from "../../../apis/death.api";

export const AssetsReport = React.forwardRef((props, ref) => {
  const { runWithLoader } = useContext(GlobalLoaderContext);

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  React.useImperativeHandle(ref, () => ({
    downloadPDF: () => {
      downloadPDF();
    },
    isDownloading: downloadLoading,
  }));
  const [assetsList, setAssetsList] = useState([]);
  const [inventoryList, setInventoryList] = useState([]);
  const [collectiblesList, setCollectiblesList] = useState([]);
  const [assetsName, setAssetsName] = useState([]);
  const [assetsValues, setAssetsValues] = useState([]);
  const [monetaryValue, setMonetaryValue] = useState(0);
  const [monetaryValueCollection, setMonetaryValueCollection] = useState(0);
  const [monetaryValueInventory, setMonetaryValueInventory] = useState(0);
  const [clientName, setClientName] = useState("");
  const [pdfChartImage, setPdfChartImage] = useState(null);
  const chartRef = useRef(null);

  const totalAssetsValue = (
    +monetaryValue +
    +monetaryValueCollection +
    +monetaryValueInventory
  ).toFixed(2);

  const getAssetsList = async () => {
    try {
      setLoading(true);
      const userRole =
        localStorage.getItem("role") != "undefined" &&
        JSON.parse(localStorage.getItem("role"));
      const ID = localStorage.getItem("accessId");
      let datares = await FETCH.post({
        url: "client-module-service/client-module/list",
        id:
          userRole === "Financial Advisor Client"
            ? `?module=Asset&page=1&limit=10&clientId=${ID}`
            : `?module=Asset&page=1&limit=10`,
        body: {},
      });
      if (datares && datares.records) {
        setAssetsList(datares.records);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const getInventoryList = async () => {
    try {
      setLoading(true);
      const userRole =
        localStorage.getItem("role") != "undefined" &&
        JSON.parse(localStorage.getItem("role"));
      const ID = localStorage.getItem("accessId");
      let datares = await FETCH.post({
        url: "client-module-service/client-module/list",
        id:
          userRole === "Financial Advisor Client"
            ? `?module=PROPERTY&page=1&limit=10&clientId=${ID}`
            : `?module=PROPERTY&page=1&limit=10`,
        body: {},
      });
      if (datares && datares.records) {
        const mappedRecords = datares.records.map((item) => ({
          ...item,
          "Name of Asset": item.name,
          "Account Type/Titled": item.module,
          "Held Where": item.city,
          "Monetary Value": item.total,
        }));
        setInventoryList(mappedRecords);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const getCollectiblesList = async () => {
    try {
      setLoading(true);
      let datares = await DEATH_API.getCollectableValues();
      if (datares && datares.message) {
        const mappedRecords = datares.message.map((item, idx) => ({
          module: "Collectible",
          "Name of Asset": item.sub_type,
          "Account Type/Titled": "Collectible",
          "Held Where": null,
          "Monetary Value": item.monetary,
          "Value as of Date": null,
          id: idx + 1,
        }));
        setCollectiblesList(mappedRecords);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const names = [];
    const values = [];

    const processItems = (items) => {
      if (items && items.length > 0) {
        items.forEach((item) => {
          names.push(item["Name of Asset"] || "Unknown");
          values.push(item["Monetary Value"] ? item["Monetary Value"] : 0);
        });
      }
    };

    processItems(assetsList);
    processItems(inventoryList);
    processItems(collectiblesList);

    setAssetsName(names);
    setAssetsValues(values);
  }, [assetsList, inventoryList, collectiblesList]);

  const fetchAllModuleDataForPDF = async (moduleName) => {
    let allData = [];
    let page = 1;
    let hasMore = true;
    const limit = 50;
    const userRole =
      localStorage.getItem("role") != "undefined" &&
      JSON.parse(localStorage.getItem("role"));
    const ID = localStorage.getItem("accessId");

    while (hasMore) {
      let datares = await FETCH.post({
        url: "client-module-service/client-module/list",
        id:
          userRole === "Financial Advisor Client"
            ? `?module=${moduleName}&page=${page}&limit=${limit}&clientId=${ID}`
            : `?module=${moduleName}&page=${page}&limit=${limit}`,
        body: {},
      });

      if (datares && datares.records && datares.records.length > 0) {
        allData = [...allData, ...datares.records];
        if (datares.records.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }
    return allData;
  };

  const downloadPDF = async () => {
    try {
      setDownloadLoading(true);
      if (props.onDownloadLoading) props.onDownloadLoading(true);

      // 1. Fetch all assets incrementally
      const allAssets = await fetchAllModuleDataForPDF("Asset");

      // Fetch all Inventory and map keys
      const allInventoryRaw = await fetchAllModuleDataForPDF("PROPERTY");
      const allInventory = allInventoryRaw.map((item) => ({
        ...item,
        "Name of Asset": item.name,
        "Account Type/Titled": item.module,
        "Held Where": item.city,
        "Monetary Value": item.total,
      }));

      // Fetch all Collectibles (using DEATH_API as it seems to return all)
      let allCollectibles = [];
      try {
        let collectiblesRes = await DEATH_API.getCollectableValues();
        if (collectiblesRes && collectiblesRes.message) {
          allCollectibles = collectiblesRes.message.map((item, idx) => ({
            module: "Collectible",
            "Name of Asset": item.sub_type,
            "Account Type/Titled": "Collectible",
            "Held Where": null,
            "Monetary Value": item.monetary,
            "Value as of Date": null,
            id: idx + 1,
          }));
        }
      } catch (e) {
        console.log("Error fetching collectibles for PDF", e);
      }

      // 2. Update state to reflect all data for chart and table
      setAssetsList(allAssets);
      setInventoryList(allInventory);
      setCollectiblesList(allCollectibles);

      // 3. Wait for chart and hidden component to re-render with full data
      setTimeout(async () => {
        if (chartRef.current) {
          const chartImage = chartRef.current.chartInstance.toBase64Image();
          setPdfChartImage(chartImage);

          // Wait for the hidden template to update with the new chart image
          setTimeout(async () => {
            const element = document.getElementById("pdf-report-container");
            if (!element) {
              console.error("PDF container not found");
              setDownloadLoading(false);
              if (props.onDownloadLoading) props.onDownloadLoading(false);
              return;
            }

            const canvas = await html2canvas(element, {
              scale: 2, // Sharp enough for PDF, safe for performance
              useCORS: true,
              logging: false,
              allowTaint: true,
              backgroundColor: "#ffffff",
              imageTimeout: 0,
              removeContainer: true,
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgProps = pdf.getImageProperties(imgData);

            const margin = 15; // 15mm margin
            const contentWidth = pdfWidth - 2 * margin;
            const contentHeight =
              (imgProps.height * contentWidth) / imgProps.width;
            const pageHeight = pdfHeight - 2 * margin; // Available height per page

            let heightLeft = contentHeight;
            let position = margin; // Initial top margin

            // Function to add white margins to current page
            const addMargins = () => {
              pdf.setFillColor(255, 255, 255);
              pdf.rect(0, 0, pdfWidth, margin, "F"); // Top
              pdf.rect(0, pdfHeight - margin, pdfWidth, margin, "F"); // Bottom
            };

            // Page 1
            pdf.addImage(
              imgData,
              "PNG",
              margin,
              position,
              contentWidth,
              contentHeight
            );
            addMargins();
            heightLeft -= pageHeight;

            // Subsequent Pages
            while (heightLeft > 0) {
              pdf.addPage();
              position = margin - (contentHeight - heightLeft);
              pdf.addImage(
                imgData,
                "PNG",
                margin,
                position,
                contentWidth,
                contentHeight
              );
              addMargins();
              heightLeft -= pageHeight;
            }

            pdf.save(`Assets_Report_${moment().format("YYYYMMDD")}.pdf`);
            setDownloadLoading(false);
            if (props.onDownloadLoading) props.onDownloadLoading(false);
          }, 800);
        } else {
          setDownloadLoading(false);
          if (props.onDownloadLoading) props.onDownloadLoading(false);
        }
      }, 1000);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setDownloadLoading(false);
      if (props.onDownloadLoading) props.onDownloadLoading(false);
    }
  };

  const data = {
    labels: assetsName && assetsName,
    datasets: [
      {
        label: "Monetary Value",
        data: assetsValues && assetsValues,
        fill: false,
        backgroundColor:
          assetsValues &&
          assetsValues.map(
            (_, index) => ChartColors[index % ChartColors.length] // Cycle through ChartColors
          ),
        borderColor:
          assetsValues &&
          assetsValues.map(
            (_, index) => ChartColors[index % ChartColors.length] // Same for borderColor
          ),
      },
    ],
  };
  const getMonetaryValueAssets = async () => {
    try {
      setLoading(true);
      setMonetaryValue(0);
      const moduleName = "Assets";
      let data = await DASHBOARD_API.fetchMonetaryValueofModule(moduleName);
      if (
        data &&
        data.data &&
        data.data[0] &&
        data.data[0].monetary &&
        data.data[0].monetary !== null
      ) {
        setMonetaryValue(data.data[0].monetary);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };
  const getMonetaryValueInventory = async () => {
    try {
      setLoading(true);
      setMonetaryValueInventory(0);
      const moduleName = "Property";
      let data = await DASHBOARD_API.fetchMonetaryValueofModule(moduleName);
      if (
        data &&
        data.data &&
        data.data[0] &&
        data.data[0].monetary &&
        data.data[0].monetary !== null
      ) {
        setMonetaryValueInventory(data.data[0].monetary);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };
  const getMonetaryValueCollection = async () => {
    try {
      setLoading(true);
      setMonetaryValueCollection(0);
      const moduleName = "Collection";
      let data = await DASHBOARD_API.fetchMonetaryValueofModule(moduleName);
      if (
        data &&
        data.data &&
        data.data[0] &&
        data.data[0].monetary &&
        data.data[0].monetary !== null
      ) {
        setMonetaryValueCollection(data.data[0].monetary);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };
  const getAll = async () => {
    setLoading(true);
    await runWithLoader(
      async () => {
        await getMonetaryValueAssets();
        await getMonetaryValueCollection();
        await getMonetaryValueInventory();
      },
      () => {
        // 👇 This runs only if global loader actually shows after 4s
        setLoading(false);
      }
    );
    setLoading(false);
  };
  useEffect(() => {
    getAssetsList();
    getInventoryList();
    getCollectiblesList();
    getAll();
    const name = getFAClientName();
    if (name) setClientName(name);
  }, [props.sharedState]);

  return (
    <React.Fragment>
      <div className="reportCard1">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 className="Reportheading">Assets</h3>
        </div>
        <div className="asset-home-icon-container">
          <div className="asset-home-icon">
            <img src={assetHomeIcon} alt="" />
          </div>
          <span
            style={{
              fontSize: "28px",
              fontWeight: "500",
              wordBreak: "break-all",
            }}
          >
            $
            {(+totalAssetsValue).toFixed(2).toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          </span>
        </div>
        <div className="reprotcardRow">
          <div>
            <div className="reportrow">
              <div className="reportrowData">
                <span className="reportTitle"> Client Name</span>
                <span className="reportsubTitle">
                  {clientName || localStorage.getItem("User") || ""}
                </span>
              </div>
              <div className="reportrowData">
                <span className="reportTitle"> Plan Nickname </span>
                <span className="reportsubTitle"> First Plan</span>
              </div>
            </div>
            <div className="reportrow2">
              <div className="reportrowData">
                <span className="reportTitle"> Today's Date </span>
                <span className="reportsubTitle">
                  {" "}
                  {moment().format("MM/DD/YYYY")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="reportCard2" style={{ position: "relative" }}>
        <h1 className="liablitycardheading">All Assets</h1>
        <div style={{ marginTop: "1rem" }}>
          <Bar
            ref={chartRef}
            data={data}
            height={300}
            options={{
              legend: {
                display: false,
              },
              tooltips: {
                callbacks: {
                  label: function (tooltipItem, chartData) {
                    const datasetLabel =
                      chartData.datasets[tooltipItem.datasetIndex].label || "";
                    const value = tooltipItem.yLabel;
                    const formattedValue = new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(value);
                    return `${datasetLabel}: ${formattedValue}`;
                  },
                },
              },
              maintainAspectRatio: false,
              responsive: true,
              devicePixelRatio: 2, // Sharper chart rendering
              scales: {
                xAxes: [
                  {
                    gridLines: {
                      color: "rgba(0, 0, 0, 0)",
                    },
                  },
                ],
                yAxes: [
                  {
                    ticks: {
                      callback: function (value) {
                        return new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                  },
                ],
              },
            }}
          />
        </div>
        <NewLoader isLoading={loading} />
      </div>

      {/* Hidden PDF Report Template */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <div id="pdf-report-container">
          <AssetsPDFReport
            assetsList={assetsList}
            inventoryList={inventoryList}
            collectiblesList={collectiblesList}
            totalAssets={(+totalAssetsValue).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            clientName={clientName || localStorage.getItem("User") || ""}
            chartImage={pdfChartImage}
          />
        </div>
      </div>
    </React.Fragment>
  );
});
